import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useAppSelector } from '../app/hooks';
import {
  Challenge,
  Locale,
  ProgrammingLanguage,
  TaskId,
} from '../services/types';

export interface FileState {
  filename: string;
  content: string;
}

export interface TaskState {
  activeVariant: { language: Locale; framework: ProgrammingLanguage };
  solutions: Record<ProgrammingLanguage, Array<FileState>>;
}

export interface SolutionState {
  activeTask?: string;
  tasks: Record<TaskId, TaskState>;
}

const initialState: SolutionState = {
  activeTask: undefined,
  tasks: {},
};

const defaultDescriptionLocale = 'en';

const hasLocale =
  (expectedLocale: string) =>
  ({ context: { language } }: { context: { language: string } }) =>
    language.startsWith(expectedLocale);

const solution = createSlice({
  name: 'solution',
  initialState,
  reducers: {
    resetSolutions() {
      return initialState;
    },
    changeActiveTask(state, action: PayloadAction<{ task: Challenge }>) {
      const { task } = action.payload;
      state.activeTask = task.id;

      // Initialize state for new tasks
      if (!state.tasks[task.id]) {
        const firstTaskVariant =
          task.variants.find(hasLocale(defaultDescriptionLocale)) ??
          task.variants[0];

        state.tasks[task.id] = {
          activeVariant: firstTaskVariant.context,
          solutions: {
            [firstTaskVariant.context.framework]: [
              {
                filename: '',
                content:
                  firstTaskVariant?.initial_code ||
                  firstTaskVariant?.initialCode ||
                  '',
              },
            ],
          },
        };
      }
    },
    changeActiveLanguage(
      state,
      action: PayloadAction<{ language: ProgrammingLanguage; task: Challenge }>,
    ) {
      if (!state.activeTask) {
        return;
      }

      const { language, task } = action.payload;
      const activeTask = state.tasks[state.activeTask];

      const newVariant =
        task.variants.find(
          (variant) =>
            variant.context.framework === language &&
            variant.context.language === activeTask.activeVariant.language,
        ) ??
        task.variants.find(
          (variant) => variant.context.framework === language,
        ) ??
        task.variants[0];

      activeTask.activeVariant = newVariant.context;

      // Initialize state for new languages within the task
      if (!activeTask.solutions[language]) {
        activeTask.solutions[language] = [
          {
            filename: '',
            content: newVariant?.initial_code || newVariant?.initialCode || '',
          },
        ];
      }
    },
    changeSolution(state, action: PayloadAction<{ solution: string }>) {
      const { activeTask } = state;

      if (!activeTask) {
        return;
      }

      const { activeVariant } = state.tasks[activeTask];

      state.tasks[activeTask].solutions[activeVariant.framework][0].content =
        action.payload.solution;
    },
  },
});

export function useActiveTaskId() {
  return useAppSelector((state) => state.solution.activeTask);
}

export function useActiveVariant() {
  return useAppSelector((state) => {
    const { activeTask } = state.solution;

    return activeTask
      ? state.solution.tasks[activeTask].activeVariant
      : { language: undefined, framework: undefined };
  });
}

export function useSolution() {
  return useAppSelector((state) => {
    const { activeTask } = state.solution;

    if (!activeTask) {
      return '';
    }

    const { activeVariant } = state.solution.tasks[activeTask];

    return state.solution.tasks[activeTask].solutions[
      activeVariant.framework
    ][0].content;
  });
}

export function useTasksSolutions() {
  return useAppSelector((state) => state.solution.tasks);
}

export const {
  changeActiveTask,
  changeActiveLanguage,
  changeSolution,
  resetSolutions,
} = solution.actions;

export default solution.reducer;
